import React, { useState, useEffect } from 'react';
import { Video, User, Clock, Zap, ExternalLink, CheckCircle } from 'lucide-react';
import { UniversalVideoSession } from '../../templates/universal/UniversalVideoSession';

interface DemoGuestProps {
  sessionId: string;
  roomId?: string;
  guestName?: string;
}

interface DemoSession {
  sessionId: string;
  roomId: string;
  hostName: string;
  guestName: string;
  status: string;
  startTime: string;
}

export const DemoGuest: React.FC<DemoGuestProps> = ({ sessionId, roomId, guestName }) => {
  const [session, setSession] = useState<DemoSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [customGuestName, setCustomGuestName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [usageWarning, setUsageWarning] = useState<string | null>(null);

  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const finalRoomId = roomId || urlParams.get('roomId') || '';
  const initialGuestName = guestName || urlParams.get('guestName') || 'Guest';
  const isAdmin = urlParams.get('isAdmin') === 'true';

  useEffect(() => {
    // Skip session loading and create a mock session directly
    // since we're using the universal template system
    const mockSession: DemoSession = {
      sessionId,
      roomId: finalRoomId,
      hostName: 'Demo Host',
      guestName: initialGuestName,
      status: 'active',
      startTime: new Date().toISOString()
    };
    
    setSession(mockSession);
    setLoading(false);
    setCustomGuestName(initialGuestName);
    
    // Skip usage checks for admin sessions
    if (!isAdmin) {
      checkUsageStatus();
    }
  }, [sessionId, finalRoomId, initialGuestName, isAdmin]);

  const checkUsageStatus = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/status/check');
      if (response.ok) {
        const data = await response.json();
        
        if (!data.allowed) {
          setUsageWarning('This demo session has reached its time limit. The host can sign up for unlimited access.');
        } else if (data.remaining.minutes <= 3) {
          setUsageWarning(`${data.remaining.minutes} minutes remaining in this demo`);
        }
      }
    } catch (error) {
      console.error('Error checking usage:', error);
    }
  };

  const handleJoinSession = () => {
    if (!customGuestName.trim()) {
      setShowNameInput(true);
      return;
    }

    console.log('[DemoGuest] Starting demo video session directly with UniversalVideoSession');
    
    // Skip all external session creation and go straight to the video session
    setSessionStarted(true);
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customGuestName.trim()) {
      setSessionStarted(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Session...</h2>
          <p className="text-gray-600">Connecting to your video call</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Unavailable</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-700">
              Want to create your own video demo?
            </p>
          </div>
          
          <a
            href="/public/demo"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Start Your Own Demo
          </a>
        </div>
      </div>
    );
  }

  if (sessionStarted && session) {
    return (
      <div className="min-h-screen bg-gray-900">
        {/* Admin Mode Indicator */}
        {isAdmin && (
          <div className="fixed top-4 left-4 right-4 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center justify-between">
            <span className="text-sm font-medium">🔧 Admin Mode - Unlimited Access</span>
            <span className="text-green-100 text-sm">No time limits or session restrictions</span>
          </div>
        )}
        
        {/* Usage Warning Overlay */}
        {usageWarning && !isAdmin && (
          <div className="fixed top-4 left-4 right-4 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center justify-between">
            <span className="text-sm font-medium">{usageWarning}</span>
            <a
              href="https://app.customerconnect.live/signup"
              className="text-white hover:text-orange-100 underline text-sm font-medium"
            >
              Get Unlimited
            </a>
          </div>
        )}

        {/* Video Session */}
        <UniversalVideoSession
          roomId={finalRoomId}
          requestId={sessionId}
          agentName={session.hostName}
          participantType="customer"
          customerName={customGuestName}
          sessionDetails={{
            title: 'Demo Video Call',
            category: 'demo',
            urgency: 'medium',
            description: 'Public demo session'
          }}
          isDemo={true}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 p-4">
      {/* Usage Warning */}
      {usageWarning && (
        <div className="max-w-lg mx-auto mb-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 flex items-center">
            <Clock className="w-5 h-5 text-orange-600 mr-2" />
            <span className="text-sm font-medium text-orange-800">{usageWarning}</span>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Video className="w-8 h-8 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {session ? `Join ${session.hostName}'s call` : 'Join Video Call'}
          </h1>
          <p className="text-gray-600 mb-4">
            You're invited to a demo video session
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-center text-sm text-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              <span>No downloads required • Works in your browser</span>
            </div>
          </div>
        </div>

        {/* Name Input */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <form onSubmit={handleNameSubmit}>
            <div className="flex items-center mb-4">
              <User className="w-5 h-5 text-gray-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Enter Your Name</h3>
            </div>
            
            <input
              type="text"
              value={customGuestName}
              onChange={(e) => setCustomGuestName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
              placeholder="Your name"
              maxLength={50}
              required
            />

            <button
              type="submit"
              disabled={!customGuestName.trim()}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-75 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <Video className="w-5 h-5 mr-2" />
              Join Video Call
            </button>
          </form>
        </div>

        {/* Demo Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">What to expect:</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
              <span>HD video and audio quality</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
              <span>Screen sharing capabilities</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
              <span>Works on mobile and desktop</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
              <span>Secure and private connection</span>
            </div>
          </div>
        </div>

        {/* Try Your Own */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
            <Zap className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Love this experience?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Create your own video demo in seconds
          </p>
          <div className="flex space-x-3">
            <a
              href="/public/demo"
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Try Demo
            </a>
            <a
              href="https://app.customerconnect.live/signup"
              className="flex-1 bg-white border border-blue-300 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium flex items-center justify-center"
            >
              Sign Up <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-gray-500">
          <p>
            Powered by CustomerConnect • Professional video conferencing
          </p>
        </div>
      </div>
    </div>
  );
};